document.addEventListener("DOMContentLoaded", () => {
  const section = document.querySelector("#frontpage-neuigkeiten");
  const gridShell = section?.querySelector(".highlight-grid-shell");
  const grid = section?.querySelector(".highlight-grid");
  const templates = section
    ? new Map(
        Array.from(
          section.querySelectorAll("template[data-frontpage-news-template]")
        ).map((template) => [
          template.dataset.frontpageNewsTemplate || "",
          template.innerHTML,
        ])
      )
    : new Map();
  const rail = section?.querySelector(".highlight-selector__rail");
  const selectorItems = section
    ? Array.from(section.querySelectorAll(".highlight-selector__item"))
    : [];
  const prefersReducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (!section || !gridShell || !grid) {
    return;
  }

  let revealObserver;
  let motionRefreshFrame;

  const getCards = () => Array.from(section.querySelectorAll(".highlight-eq"));
  const rowTopTolerance = 6;
  const motionProfiles = {
    default: {
      rowDelayStep: 126,
      columnDelayStep: 60,
      maxCardDelay: 300,
    },
    fast: {
      rowDelayStep: 66,
      columnDelayStep: 26,
      maxCardDelay: 132,
    },
  };

  const getMotionProfile = (motionMode = "default") =>
    motionProfiles[motionMode] || motionProfiles.default;

  const setMotionMode = (motionMode = "default") => {
    section.dataset.highlightMotion = motionMode;
  };

  const syncCardMotionDelays = (
    cards = getCards(),
    motionProfile = getMotionProfile(section.dataset.highlightMotion)
  ) => {
    const { rowDelayStep, columnDelayStep, maxCardDelay } = motionProfile;
    const rowTops = [];
    const rowCounts = [];

    cards.forEach((card, index) => {
      const currentTop = card.offsetTop;
      let rowIndex = rowTops.findIndex(
        (rowTop) => Math.abs(rowTop - currentTop) <= rowTopTolerance
      );

      if (rowIndex === -1) {
        rowTops.push(currentTop);
        rowCounts.push(0);
        rowIndex = rowTops.length - 1;
      }

      const columnIndex = rowCounts[rowIndex];
      const cardDelay = Math.min(
        rowIndex * rowDelayStep + columnIndex * columnDelayStep,
        maxCardDelay
      );

      rowCounts[rowIndex] += 1;

      card.style.setProperty("--highlight-index", index);
      card.style.setProperty("--highlight-delay", `${cardDelay}ms`);
    });
  };

  const refreshCardMotionDelays = () => {
    if (prefersReducedMotion) {
      return;
    }

    window.cancelAnimationFrame(motionRefreshFrame);
    motionRefreshFrame = window.requestAnimationFrame(() => {
      syncCardMotionDelays(getCards(), getMotionProfile(section.dataset.highlightMotion));
    });
  };

  const prepareCardsForReveal = (cards = getCards(), options = {}) => {
    const { motionMode = "default" } = options;

    if (revealObserver) {
      revealObserver.disconnect();
      revealObserver = undefined;
    }

    if (cards.length === 0) {
      setMotionMode(motionMode);
      section.classList.remove("is-motion-ready");
      return { cards, shouldAnimate: false };
    }

    cards.forEach((card, index) => {
      card.classList.remove("is-visible");
      card.style.setProperty("--highlight-index", index);
    });

    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      setMotionMode(motionMode);
      section.classList.remove("is-motion-ready");
      cards.forEach((card) => {
        card.classList.add("is-visible");
      });
      return { cards, shouldAnimate: false };
    }

    setMotionMode(motionMode);
    syncCardMotionDelays(cards, getMotionProfile(motionMode));
    section.classList.add("is-motion-ready");
    return { cards, shouldAnimate: true };
  };

  const startReveal = (cards, options = {}) => {
    const { immediate = false } = options;

    if (cards.length === 0) {
      return;
    }

    if (immediate) {
      window.requestAnimationFrame(() => {
        cards.forEach((card) => {
          card.classList.add("is-visible");
        });
      });
      return;
    }

    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add("is-visible");
          revealObserver?.unobserve(entry.target);
        });
      },
      {
        threshold: 0.14,
        rootMargin: "0px 0px -8% 0px",
      }
    );

    window.requestAnimationFrame(() => {
      cards.forEach((card) => {
        revealObserver?.observe(card);
      });
    });
  };

  const revealCards = () => {
    const { cards, shouldAnimate } = prepareCardsForReveal();

    if (!shouldAnimate) {
      return;
    }

    startReveal(cards);
  };

  // Die Reiterleiste bricht nicht mehr um, sondern scrollt (siehe
  // _frontpage.scss). Nur die Leiste scrollen, nie die Seite: scrollIntoView
  // würde den Viewport vertikal mitziehen.
  const centerRailItem = (item, behavior = "smooth") => {
    if (!rail || !item || rail.scrollWidth <= rail.clientWidth + 1) {
      return;
    }

    const railRect = rail.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const delta =
      itemRect.left +
      itemRect.width / 2 -
      (railRect.left + railRect.width / 2);

    if (Math.abs(delta) < 1) {
      return;
    }

    const left = rail.scrollLeft + delta;

    if (typeof rail.scrollTo === "function") {
      rail.scrollTo({
        left,
        behavior: prefersReducedMotion ? "auto" : behavior,
      });
    } else {
      rail.scrollLeft = left;
    }
  };

  const setActiveItem = (nextActiveItem) => {
    selectorItems.forEach((item) => {
      const isActive = item === nextActiveItem;
      item.classList.toggle("is-active", isActive);

      if (isActive) {
        item.setAttribute("aria-current", "true");
      } else {
        item.removeAttribute("aria-current");
      }
    });

    centerRailItem(nextActiveItem);
  };

  const replaceGridContent = (nextHtml) => {
    grid.innerHTML = nextHtml;
    const { cards, shouldAnimate } = prepareCardsForReveal(getCards(), {
      motionMode: "fast",
    });

    if (!shouldAnimate) {
      return;
    }

    window.requestAnimationFrame(() => {
      startReveal(cards, { immediate: true });
    });
  };

  const getCategoryFromUrl = (url) => {
    const nextUrl = new URL(url, window.location.origin);
    return (
      nextUrl.searchParams.get("frontpage_news_category") || "veranstaltungen"
    );
  };

  const switchCategory = (item, options = {}) => {
    const { skipHistory = false } = options;
    const nextCategory =
      item.dataset.frontpageNewsCategory || "veranstaltungen";
    const nextHtml = templates.get(nextCategory);

    if (typeof nextHtml !== "string") {
      window.location.href = item.href;
      return;
    }

    setActiveItem(item);

    if (gridShell) {
      gridShell.setAttribute("aria-busy", "true");
    }

    replaceGridContent(nextHtml);

    if (!skipHistory && item.href) {
      window.history.pushState(
        { frontpageNewsCategory: nextCategory },
        "",
        item.href
      );
    }

    if (gridShell) {
      gridShell.setAttribute("aria-busy", "false");
    }
  };

  revealCards();
  centerRailItem(
    selectorItems.find((item) => item.classList.contains("is-active")),
    "auto"
  );
  window.addEventListener("resize", refreshCardMotionDelays);

  selectorItems.forEach((item) => {
    item.addEventListener("click", (event) => {
      event.preventDefault();

      if (
        item.classList.contains("is-active") &&
        gridShell?.getAttribute("aria-busy") !== "true"
      ) {
        return;
      }

      switchCategory(item);
    });
  });

  window.addEventListener("popstate", () => {
    const targetCategory = getCategoryFromUrl(window.location.href);
    const targetItem =
      selectorItems.find(
        (item) =>
          (item.dataset.frontpageNewsCategory || "veranstaltungen") ===
          targetCategory
      ) || selectorItems[0];

    if (!targetItem) {
      return;
    }

    if (
      targetItem.classList.contains("is-active") &&
      gridShell?.getAttribute("aria-busy") !== "true"
    ) {
      return;
    }

    switchCategory(targetItem, { skipHistory: true });
  });
});
